import { useMutation } from "@tanstack/react-query";
import { apiClient } from "~/lib/client";
import { handleError, handleResponse } from "~/utils/response";
import { getQueryClient } from "~/providers/query.provider";
import { toast } from "sonner";

// Define types
export interface ChatRequestData {
    message: string;
    thread_id?: string;
    agent?: string; // Add agent parameter
}

interface Message {
    content: string;
    additional_kwargs: Record<string, any>;
    response_metadata: Record<string, any>;
    type: 'human' | 'ai';
    name: string | null;
    id: string | null;
    example: boolean;
}

interface FollowUpQuestion extends Message {
    tool_calls: any[];
    invalid_tool_calls: any[];
    usage_metadata: any;
    question: string;
    options: string[];
}

export interface ChatResponseData {
    messages: Message[];
    follow_up_question: FollowUpQuestion;
    run_id: string;
    user_id: string;
    thread_id: string;
    end: boolean;
}

export interface ChatResponse {
    success: boolean;
    data?: ChatResponseData;
    error?: string | null;
}

// Rest of the code remains the same
export const chatKeys = {
    all: ['chat'] as const,
    messages: () => [...chatKeys.all, 'messages'] as const,
};

export const useChatMutation = () => {
    const queryClient = getQueryClient();

    return useMutation<ChatResponse, unknown, ChatRequestData>({
        mutationFn: async (variables: ChatRequestData) => {
            try {
                // Determine the agent endpoint based on the agent parameter
                const agent = variables.agent || "alfie-chat"; // Default to alfie-chat
                const endpoint = `/agents/${agent}/invoke`;
                
                // Create request body without the agent field
                const { agent: _, ...requestBody } = variables;
                
                const response = await apiClient(endpoint, {
                    method: "POST",
                    body: JSON.stringify(requestBody),
                });

                return await handleResponse<ChatResponseData>(response);
            } catch (error) {
                return handleError(error);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: chatKeys.messages() });
        },
        onError: () => {
            toast.error("Failed to send message");
        },
    });
};